/* ===== Dues (Cuotas Sociales) Page ===== */

import { useEffect, useMemo, useState } from 'react'
import type { OrgDuesSettings } from '@zetyar/domain'
import { CreditCard, Settings } from 'lucide-react'
import { useApp } from '@/presentation/providers/useApp'
import { Card } from '@/presentation/components/ui/Card'
import { Button } from '@/presentation/components/ui/Button'
import { createWebTenantContext } from '@/infrastructure/repositories/announcements'
import { webDuesRepository } from '@/infrastructure/repositories/dues'
import { DuesAdminView } from './DuesAdminView'
import { DuesSettingsModal } from './DuesSettingsModal'
import { MyDuesView } from './MyDuesView'
import { duesErrorMessage } from './duesFormat'

export function DuesPage() {
  const { currentMembership, currentOrganization, profile, user, permissions } = useApp()
  const [settings, setSettings] = useState<OrgDuesSettings | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)

  // currentMembership can fall back to a membership of another organization; only use it here when
  // it belongs to the organization on screen.
  const membership =
    currentMembership &&
    currentMembership.organizationId === currentOrganization?.id &&
    currentMembership.status === 'active'
      ? currentMembership
      : null
  // Writes require an active organization_admin/treasurer membership server-side, so a super admin
  // without one gets the read-only roster.
  const canManage = Boolean(membership) && permissions.includes('dues.manage')
  const canViewAll = Boolean(profile?.isSuperAdmin) || permissions.includes('dues.view_all')
  const organizationName = currentOrganization?.name ?? 'la organización'

  const organizationId = currentOrganization?.id
  const membershipId = membership?.id
  const context = useMemo(() => {
    if (!organizationId || !profile || !user) return null
    return createWebTenantContext({ authUid: user.uid, personId: profile.id, organizationId, membershipId })
  }, [organizationId, membershipId, profile, user])

  useEffect(() => {
    if (!context) {
      setIsLoading(false)
      return
    }

    let cancelled = false
    setIsLoading(true)
    setError(null)
    webDuesRepository
      .getSettings(context)
      .then((result) => {
        if (!cancelled) setSettings(result)
      })
      .catch((caught) => {
        console.error('Error al cargar la configuración de cuotas:', caught)
        if (!cancelled) {
          setSettings(null)
          setError(duesErrorMessage(caught, 'No se pudo cargar la configuración de cuotas.'))
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [context])

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-neutral-900 dark:text-neutral-100">Cuotas sociales</h1>
          <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
            Control mensual de cuotas de {organizationName}.
          </p>
        </div>
        {canManage && settings && (
          <Button variant="outline" leftIcon={<Settings className="h-4 w-4" />} onClick={() => setIsSettingsOpen(true)}>
            Configurar cuotas
          </Button>
        )}
      </div>

      {error && (
        <div role="alert" className="rounded-[13px] border border-danger-200 bg-danger-50 px-4 py-3 text-sm text-danger-700 dark:border-danger-500/30 dark:bg-danger-500/10 dark:text-danger-300">
          {error}
        </div>
      )}

      {isLoading ? (
        <Card className="p-8 text-center text-sm text-neutral-500 dark:text-neutral-400">Cargando cuotas...</Card>
      ) : !settings ? (
        !error && (
          <Card className="p-8 text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-[14px] bg-primary-50 text-primary-600 dark:bg-primary-500/15 dark:text-primary-300">
              <CreditCard className="h-6 w-6" />
            </div>
            <h2 className="text-lg font-bold text-neutral-900 dark:text-neutral-100">La tesorería aún no configura las cuotas</h2>
            <p className="mx-auto mt-2 max-w-md text-sm text-neutral-500 dark:text-neutral-400">
              {canManage
                ? 'Define el monto mensual, el día de vencimiento y los datos de transferencia para comenzar a registrar pagos.'
                : 'Cuando la tesorería defina el monto y los datos de pago, aquí verás el estado de tu cuota.'}
            </p>
            {canManage && (
              <Button className="mt-6" leftIcon={<Settings className="h-4 w-4" />} onClick={() => setIsSettingsOpen(true)}>
                Configurar cuotas
              </Button>
            )}
          </Card>
        )
      ) : context ? (
        <>
          {membership && (
            <MyDuesView context={context} membershipId={membership.id} settings={settings} organizationName={organizationName} />
          )}
          {canViewAll && (
            <DuesAdminView context={context} settings={settings} canManage={canManage} organizationName={organizationName} />
          )}
        </>
      ) : null}

      {context && canManage && (
        <DuesSettingsModal
          isOpen={isSettingsOpen}
          onClose={() => setIsSettingsOpen(false)}
          context={context}
          settings={settings}
          onSaved={(saved) => {
            setSettings(saved)
            setIsSettingsOpen(false)
          }}
        />
      )}
    </div>
  )
}
